import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { services } from "@/content/services";
import { industries } from "@/content/industries";
import { tools } from "@/content/tools";
import { templates } from "@/content/templates";
import { glossaryTerms } from "@/content/glossary";
import { helplines } from "@/content/helplines";

interface InternalLinksHubProps {
  /** Heading, include a relevant keyword (e.g. "Explore more HR support"). */
  heading?: string;
  intro?: string;
  /** Slugs to exclude from each column so we don't link back to the current page. */
  excludeServices?: string[];
  excludeIndustries?: string[];
  className?: string;
}

/**
 * Sitewide internal-linking hub. Drop above CTASection on any marketing or hub
 * page to expose deep links to services, industries, tools, templates and
 * glossary, improving crawl depth and topical relevance signals.
 */
export function InternalLinksHub({
  heading = "Explore more HR & employment law support",
  intro = "Jump straight to the service, sector or resource you need, every link below opens a fully written guide for UK employers.",
  excludeServices = [],
  excludeIndustries = [],
  className,
}: InternalLinksHubProps) {
  const svc = services.filter((s) => !excludeServices.includes(s.slug)).slice(0, 6);
  const ind = industries.filter((i) => !excludeIndustries.includes(i.slug)).slice(0, 6);
  const topTools = tools.slice(0, 5);
  const topTemplates = templates.slice(0, 5);
  const topGlossary = glossaryTerms.slice(0, 6);

  return (
    <section
      className={
        className ??
        "border-y border-border/60 bg-gradient-to-b from-background to-card/40 px-6 py-20 lg:px-8"
      }
      aria-labelledby="internal-hub-heading"
    >
      <div className="mx-auto max-w-7xl">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">Browse the site</p>
          <h2 id="internal-hub-heading" className="mt-3 font-serif text-3xl text-brand sm:text-4xl">
            {heading}
          </h2>
          <p className="mt-4 text-base text-muted-foreground">{intro}</p>
        </div>

        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          <HubColumn
            title="HR Services for UK employers"
            seeAll={{ to: "/services", label: "All services" }}
          >
            {svc.map((s) => (
              <HubLink key={s.slug} to="/services/$slug" params={{ slug: s.slug }} label={s.shortName} />
            ))}
          </HubColumn>

          <HubColumn
            title="HR & employment law by industry"
            seeAll={{ to: "/hr-by-industry", label: "All industries" }}
          >
            {ind.map((i) => (
              <HubLink key={i.slug} to="/hr-by-industry/$slug" params={{ slug: i.slug }} label={i.shortName} />
            ))}
          </HubColumn>

          <HubColumn
            title="Free HR calculators"
            seeAll={{ to: "/hr-calculators", label: "All calculators" }}
          >
            {topTools.map((t) => (
              <HubLink
                key={t.slug}
                to="/hr-calculators/$slug"
                params={{ slug: t.slug }}
                label={t.name}
              />
            ))}
          </HubColumn>

          <HubColumn
            title="Free HR templates"
            seeAll={{ to: "/hr-templates", label: "All templates" }}
          >
            {topTemplates.map((t) => (
              <HubLink
                key={t.slug}
                to="/hr-templates/$slug"
                params={{ slug: t.slug }}
                label={t.title}
              />
            ))}
          </HubColumn>

          <HubColumn
            title="HR & employment law glossary"
            seeAll={{ to: "/hr-glossary", label: "Full glossary" }}
          >
            {topGlossary.map((g) => (
              <HubLink
                key={g.slug}
                to="/hr-glossary/$slug"
                params={{ slug: g.slug }}
                label={g.term}
              />
            ))}
          </HubColumn>

          <HubColumn
            title="Employer helplines"
            seeAll={{ to: "/employer-helpline", label: "All helplines" }}
          >
            {helplines.filter((h) => !h.isHub).map((h) => (
              <HubLink key={h.slug} to={h.path} label={h.shortName} />
            ))}
          </HubColumn>

          <HubColumn
            title="Talk to an HR expert"
            seeAll={{ to: "/contact-hr-experts", label: "Book a free consultation" }}
          >
            <HubLink to="/hr-packages" label="See pricing & plans" />
            <HubLink to="/about-employerhq" label="Meet our senior team" />
            <HubLink to="/hr-resources" label="Resource library" />
            <HubLink to="/hr-insights" label="Latest HR insights" />
            <HubLink to="/services/hr-health-check" params={{ slug: "hr-health-check" }} label="Free HR health check" />
          </HubColumn>
        </div>
      </div>
    </section>
  );
}

function HubColumn({
  title,
  seeAll,
  children,
}: {
  title: string;
  seeAll: { to: string; label: string };
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="font-serif text-lg text-brand">{title}</h3>
      <ul className="mt-5 space-y-2.5 text-sm">{children}</ul>
      <Link
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        to={seeAll.to as any}
        className="mt-5 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-accent hover:underline"
      >
        {seeAll.label} <ArrowRight className="size-3" />
      </Link>
    </div>
  );
}

function HubLink({
  to,
  params,
  label,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  to: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: any;
  label: string;
}) {
  return (
    <li>
      <Link
        to={to}
        params={params}
        className="group flex items-center gap-2 text-foreground/80 transition hover:text-accent"
      >
        <span className="size-1 rounded-full bg-accent/60 transition group-hover:bg-accent" aria-hidden />
        {label}
      </Link>
    </li>
  );
}
